/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useId, useState } from 'react';
import type { ReactElement } from 'react';

import { Tab, TabContext, TabPanel, Tabs, Typography2 } from '@coursera/cds-core';

import Container from 'bundles/degree-description/components/shared/Container';

export type ProgramDetailsTab = {
  tabTitle: string;
  tabContents: ReactElement;
};

type Props = {
  tabs: ProgramDetailsTab[];
  sectionHeading: string;
};

const styles = {
  module: css`
    padding-top: var(--cds-spacing-400);
    padding-bottom: var(--cds-spacing-400);
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  tab: css`
    && {
      max-width: none;
      white-space: nowrap;
    }
  `,
  panel: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: 8px;
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
  `,
};

const ProgramDetailsSection = ({ tabs, sectionHeading }: Props) => {
  const [selectedTab, setSelectedTab] = useState('0');
  const headingId = useId();

  if (tabs.length === 0) return null;

  return (
    <div css={styles.module}>
      <Container>
        <Typography2 variant="subtitleLarge" component="h2" id={headingId} css={styles.heading}>
          {sectionHeading}
        </Typography2>
        <TabContext value={selectedTab}>
          <Tabs onChange={setSelectedTab} aria-labelledby={headingId}>
            {tabs.map((tab, index) => (
              <Tab label={tab.tabTitle} value={String(index)} key={tab.tabTitle} css={styles.tab} />
            ))}
          </Tabs>
          {tabs.map((tab, index) => (
            <TabPanel value={String(index)} key={tab.tabTitle}>
              <div css={styles.panel}>{tab.tabContents}</div>
            </TabPanel>
          ))}
        </TabContext>
      </Container>
    </div>
  );
};

export default ProgramDetailsSection;
